/**
 * Decides what an institute's own domain may SHOW.
 *
 * In order:
 *   1. platform host (or a bare IP)  -> children, untouched
 *   2. tenant lookup in flight       -> fallback (never a platform page)
 *   3. domain belongs to nobody, or
 *      the lookup failed             -> "Site not available", no branding.
 *                                       Fails CLOSED: an unknown domain must
 *                                       never show EduExpertAI pages.
 *   4. master switched it off        -> branded "Unavailable" page
 *                                       (services/tenantRoutes.tenantAccessBlock)
 *   5. platform-only page / visitor
 *      on an app page                -> redirect (tenantRedirectFor)
 *
 * Security is NOT decided here. The server refuses the API calls for every one
 * of these cases (tenancy/tenant_access.py); this is about what a visitor sees.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import SiteUnavailable from '@/components/shared/SiteUnavailable';
import { useBranding } from '@/contexts/BrandingContext';
import { isPlatformHost } from '@/http';
import { isSignedInHere as isSignedIn } from '@/services/signedInHere';

const TenantWebsite = React.lazy(() => import('@/features/site/public/TenantWebsite'));
import { tenantAccessBlock, tenantRedirectFor } from '@/services/tenantRoutes';


/** A bare IPv4 address: the server reached directly while testing, not a tenant. */
const isIpHost = (): boolean =>
  typeof window !== 'undefined' && /^\d{1,3}(\.\d{1,3}){3}$/.test(window.location.hostname);

interface Props {
  children: React.ReactNode;
  /** Shown while the tenant lookup is in flight on a tenant host. */
  fallback: React.ReactNode;
}

const TenantRouteGuard: React.FC<Props> = ({ children, fallback }) => {
  const location = useLocation();
  const { isPlatform, loading, access } = useBranding();

  if (isPlatformHost() || isIpHost()) return <>{children}</>;

  if (loading) return <>{fallback}</>;

  // On a non-platform host, "platform" can only mean the lookup found no
  // tenant (404) or failed. Either way this domain is not ours to dress.
  if (isPlatform) return <SiteUnavailable variant="not-found" />;

  const block = tenantAccessBlock(location.pathname, access);
  if (block) {
    return (
      <SiteUnavailable
        variant="blocked"
        message={access.message}
        offerSignIn={block === 'public' && access.portal}
      />
    );
  }

  const target = tenantRedirectFor(location.pathname, {
    isPlatform,
    signedIn: isSignedIn(),
  });
  // A signed-out visitor on an address that is not a known public one may be
  // on a page of the institute's website (/about, /faq ...): show the website,
  // which sends them to sign-in only when the address is no page of it. The
  // app's own pages are never rendered for them either way.
  if (target === '/login') {
    return (
      <React.Suspense fallback={fallback}>
        <TenantWebsite />
      </React.Suspense>
    );
  }
  if (target && target !== location.pathname) {
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

export default TenantRouteGuard;
